import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function VerifyEmailPage() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email`, { code }, { withCredentials: true });
      await checkAuth();
      toast.success('Email verified successfully!');
      navigate('/members');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/send-verification`, {}, { withCredentials: true });
      toast.success('Verification code resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light mb-2 text-center" data-testid="verify-email-heading">
            Verify Your Email
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            We've sent a 6-digit code to <span className="text-primary" data-testid="verify-email-address">{user?.email}</span>
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength="6"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                className="text-center text-2xl tracking-[0.5em]"
                data-testid="verify-code-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-button"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>
          </form>

          <div className="text-center mt-6 space-y-2">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-xs text-muted-foreground hover:text-primary"
              data-testid="resend-code-button"
            >
              {resending ? 'Sending...' : "Didn't receive it? Resend code"}
            </button>
            <div>
              <button
                onClick={() => navigate('/members')}
                className="text-xs text-muted-foreground hover:text-primary"
                data-testid="skip-verification-button"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
